import React, { useCallback, useMemo, useRef, useState } from "react";

import { InvisibleSmartCaptcha } from "@yandex/smart-captcha";

import { YANDEX_SMARTCAPTCHA_CONFIG } from "./constants";
import { SmartCaptchaContext } from "./SmartCaptchaContext";
import { SmartCaptchaProviderProps } from "./types";

export const SmartCaptchaProvider: React.FC<SmartCaptchaProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  // Виджет монтируем только когда он реально нужен (SEO #12, task 4575): пакет
  // @yandex/smart-captcha тянет внешний captcha.js при монтировании, а провайдер обёрнут
  // вокруг всего приложения — раньше скрипт грузился на каждой странице сайта.
  // Формы авторизации зовут prepareCaptcha() при открытии, поэтому к моменту отправки
  // формы капча уже готова.
  const [isCaptchaMounted, setIsCaptchaMounted] = useState(false);

  const prepareCaptcha = useCallback(() => {
    setIsCaptchaMounted(true);
  }, []);
  const resolveRef = useRef<((token: string | null) => void) | null>(null);

  const executeCaptcha = useCallback((): Promise<string | null> =>
    new Promise((resolve) => {
      // Если уже есть незавершенный промис, резолвим его с null
      if (resolveRef.current) {
        resolveRef.current(null);
        resolveRef.current = null;
      }

      // Если уже есть токен, сбрасываем его
      setToken((currentToken) => {
        if (currentToken) {
          return null;
        }
        return currentToken;
      });

      setIsLoading(true);
      setError(null);
      resolveRef.current = resolve;
      // Страховка: если кто-то вызвал капчу, не открыв форму, монтируем прямо сейчас.
      setIsCaptchaMounted(true);

      // Увеличиваем key для переинициализации компонента капчи
      setCaptchaKey((prev) =>
        prev + 1);
      setVisible(true);
    }), []);

  const handleSuccess = useCallback(
    (captchaToken: string) => {
      setToken(captchaToken);
      setIsLoading(false);
      setVisible(false);
      setError(null);
      if (resolveRef.current) {
        resolveRef.current(captchaToken);
        resolveRef.current = null;
      }
    },
    [],
  );

  const handleChallengeHidden = useCallback(() => {
    setVisible(false);
    setIsLoading(false);
    // Если challenge закрыт без успеха, токен не выдан
    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
  }, []);

  const handleTokenExpired = useCallback(() => {
    setToken(null);
    setIsLoading(false);
    setVisible(false);
  }, []);

  const handleNetworkError = useCallback(() => {
    setError(new Error("Network error"));
    setIsLoading(false);
    setVisible(false);
    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    setIsLoading(false);
    setVisible(false);
    setError(null);
    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
  }, []);

  const value = useMemo(() =>
    ({
      executeCaptcha,
      prepareCaptcha,
      token,
      isLoading,
      error,
      reset,
    }), [executeCaptcha, prepareCaptcha, token, isLoading, error, reset]);

  // Если sitekey не настроен, не рендерим каптчу
  if (!YANDEX_SMARTCAPTCHA_CONFIG.sitekey) {
    return <>{children}</>;
  }

  return (
    <SmartCaptchaContext.Provider
      value={value}
    >
      {children}
      {isCaptchaMounted && (
      <InvisibleSmartCaptcha
        key={captchaKey}
        sitekey={YANDEX_SMARTCAPTCHA_CONFIG.sitekey}
        visible={visible}
        onSuccess={handleSuccess}
        onChallengeHidden={handleChallengeHidden}
        onTokenExpired={handleTokenExpired}
        onNetworkError={handleNetworkError}
        hideShield
      />
      )}
    </SmartCaptchaContext.Provider>
  );
};
